/**
 * Cart tools: discount code input, applied-discount removal and shipping
 * calculator. Used on the cart page (main-cart-footer) and in the cart drawer.
 *
 * Re-render strategy after a cart update:
 * - inside the drawer: request the 'cart-drawer' + 'cart-icon-bubble' sections
 *   and swap #CartDrawer's contents (same replacement Dawn's cart-drawer.js does).
 * - on the cart page: reuse cart-items.getSectionsToRender()/renderContents().
 */

(() => {
  const POLL_INTERVAL_MS = 500;
  const MAX_POLL_MS = 15000;

  function sectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return parsed ? parsed.innerHTML : null;
  }

  function replaceDrawerContents(cart) {
    if (!cart || !cart.sections) return;
    const drawer = document.querySelector('#CartDrawer');
    if (drawer && cart.sections['cart-drawer']) {
      const inner = sectionInnerHTML(cart.sections['cart-drawer'], '#CartDrawer');
      if (inner !== null) drawer.innerHTML = inner;
    }
    const bubble = document.getElementById('cart-icon-bubble');
    if (bubble && cart.sections['cart-icon-bubble']) {
      const inner = sectionInnerHTML(cart.sections['cart-icon-bubble'], '.shopify-section');
      if (inner !== null) bubble.innerHTML = inner;
    }
  }

  function cartPageItems() {
    return document.querySelector('cart-items');
  }

  function sectionsToRequest(element) {
    if (element.closest('#CartDrawer')) return ['cart-drawer', 'cart-icon-bubble'];
    const cartItems = cartPageItems();
    if (cartItems && typeof cartItems.getSectionsToRender === 'function') {
      return cartItems.getSectionsToRender().map((section) => section.section);
    }
    return [];
  }

  /* Same section swap cart-items.updateQuantity performs (this theme's
     CartItems has no public renderContents) */
  function renderCartPageSections(cart) {
    const cartItems = cartPageItems();
    if (!cartItems || !cart || !cart.sections) return;
    cartItems.getSectionsToRender().forEach((section) => {
      const sectionElement = document.getElementById(section.id);
      if (!sectionElement || !cart.sections[section.section]) return;
      const elementToReplace = sectionElement.querySelector(section.selector) || sectionElement;
      const inner = sectionInnerHTML(cart.sections[section.section], section.selector || '.shopify-section');
      if (inner !== null) elementToReplace.innerHTML = inner;
    });
  }

  function renderCart(element, cart) {
    if (element.closest('#CartDrawer')) {
      replaceDrawerContents(cart);
      return;
    }
    renderCartPageSections(cart);
  }

  async function updateCartDiscounts(element, discounts) {
    const body = JSON.stringify({
      discount: discounts,
      sections: sectionsToRequest(element),
      sections_url: window.location.pathname,
    });
    const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
    return response.json();
  }

  if (!customElements.get('discount-code')) {
    customElements.define(
      'discount-code',
      class DiscountCode extends HTMLElement {
        constructor() {
          super();
          this.input = this.querySelector('input[type="text"]');
          this.button = this.querySelector('[data-discount-apply]');
          this.error = this.querySelector('.cart-discount__error');
          if (!this.input || !this.button) return;
          this.defaultPlaceholder = this.input.placeholder;
          this.button.setAttribute('type', 'button');
          this.button.addEventListener('click', this.onApply.bind(this));
          this.input.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              this.onApply();
            }
          });
          const restorePlaceholder = () => {
            if (this.input.placeholder !== this.defaultPlaceholder) {
              this.input.placeholder = this.defaultPlaceholder;
            }
          };
          this.input.addEventListener('focus', restorePlaceholder);
          this.input.addEventListener('input', restorePlaceholder);
        }

        async onApply() {
          const code = (this.input.value || '').trim();
          if (!code) {
            this.showError();
            return;
          }
          this.button.disabled = true;
          this.button.classList.add('loading');
          if (this.error) this.error.hidden = true;

          try {
            const cart = await updateCartDiscounts(this, code);
            const match = (cart.discount_codes || []).find(
              (entry) => entry.code.toLowerCase() === code.toLowerCase()
            );
            if (!match || !match.applicable) {
              this.showError();
              return;
            }
            this.input.value = '';
            if (this.dataset.appliedMessage) this.input.placeholder = this.dataset.appliedMessage;
            renderCart(this, cart);
          } catch (error) {
            console.error(error);
            this.showError();
          } finally {
            this.button.disabled = false;
            this.button.classList.remove('loading');
          }
        }

        showError() {
          if (this.error) this.error.hidden = false;
        }
      }
    );
  }

  if (!customElements.get('discount-remove')) {
    customElements.define(
      'discount-remove',
      class DiscountRemove extends HTMLElement {
        constructor() {
          super();
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.button.setAttribute('type', 'button');
          this.button.addEventListener('click', this.onRemove.bind(this));
        }

        async onRemove() {
          this.button.disabled = true;
          this.button.classList.add('loading');
          const target = (this.dataset.code || '').trim().toLowerCase();
          const remaining = Array.from(
            new Set(
              Array.from(document.querySelectorAll('discount-remove[data-code]'))
                .map((el) => (el.dataset.code || '').trim())
                .filter((code) => code && code.toLowerCase() !== target)
            )
          );

          try {
            const cart = await updateCartDiscounts(this, remaining.join(','));
            renderCart(this, cart);
          } catch (error) {
            console.error(error);
            this.button.disabled = false;
            this.button.classList.remove('loading');
          }
        }
      }
    );
  }

  function buildAddressQuery({ zip, country, province }) {
    const params = new URLSearchParams();
    if (zip) params.append('shipping_address[zip]', zip);
    if (country) params.append('shipping_address[country]', country);
    if (province) params.append('shipping_address[province]', province);
    return params.toString();
  }

  function delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /* Shopify's shipping rates endpoint is asynchronous: POST the address to
     prepare_shipping_rates, then poll async_shipping_rates until it responds
     with the calculated rates. */
  async function fetchShippingRates(address, genericError) {
    const query = buildAddressQuery(address);

    const prepareResponse = await fetch('/cart/prepare_shipping_rates.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: query,
    });
    if (prepareResponse.status === 422) {
      const data = await prepareResponse.json().catch(() => ({}));
      const fieldMessages = data && data.errors ? Object.values(data.errors).flat().join(', ') : '';
      throw new Error(fieldMessages || genericError);
    }
    if (!prepareResponse.ok) {
      throw new Error(genericError);
    }

    const start = Date.now();
    while (Date.now() - start < MAX_POLL_MS) {
      const pollResponse = await fetch(`/cart/async_shipping_rates.json?${query}`);
      if (!pollResponse.ok) {
        throw new Error(genericError);
      }
      const data = await pollResponse.json();
      if (data && Array.isArray(data.shipping_rates)) {
        return data;
      }
      await delay(POLL_INTERVAL_MS);
    }
    throw new Error(genericError);
  }

  /* Drawer quick-action panels: dismiss on outside click / Escape, and save
     the order note explicitly (the note also persists in the cart object, so
     it survives refreshes). */
  document.addEventListener('click', (event) => {
    document.querySelectorAll('.cart-drawer__action[open]').forEach((details) => {
      if (!details.contains(event.target)) details.removeAttribute('open');
    });
  });

  document.addEventListener('keyup', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll('.cart-drawer__action[open]').forEach((details) => details.removeAttribute('open'));
  });

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-note-save]');
    if (!button) return;
    const panel = button.closest('.cart-drawer__action-panel');
    const textarea = panel ? panel.querySelector('textarea[name="note"]') : null;
    if (!textarea) return;
    button.disabled = true;
    button.classList.add('loading');
    try {
      const body = JSON.stringify({ note: textarea.value });
      await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
      const details = button.closest('details');
      if (details) details.removeAttribute('open');
    } catch (error) {
      console.error(error);
    } finally {
      button.disabled = false;
      button.classList.remove('loading');
    }
  });

  if (!customElements.get('shipping-calculator')) {
    customElements.define(
      'shipping-calculator',
      class ShippingCalculator extends HTMLElement {
        constructor() {
          super();
          this.button = this.querySelector('[data-shipping-submit]');
          this.country = this.querySelector('select[name="address[country]"]');
          this.province = this.querySelector('select[name="address[province]"]');
          this.provinceWrapper = this.querySelector('[data-province-wrapper]');
          this.zip = this.querySelector('input[autocomplete="postal-code"]');
          this.results = this.querySelector('.shipping-calculator__results');
          this.message = this.querySelector('.shipping-calculator__message');
          this.count = this.querySelector('[data-rates-count]');
          this.mixedMessage = this.querySelector('[data-mixed-cart-message]');
          if (!this.button || !this.country) return;

          if (window.Shopify && Shopify.CountryProvinceSelector && this.province && this.provinceWrapper) {
            /* Pass elements, not ids: the quickview modal clones this markup
               with identical block-scoped ids, and id lookups would point a
               second instance at the first one's selects */
            new Shopify.CountryProvinceSelector(this.country, this.province, {
              hideElement: this.provinceWrapper,
            });
          }

          this.applyProvinceFilter();
          this.country.addEventListener('change', () => {
            /* CountryProvinceSelector repopulates the province list on this same
               event; filter after it has run */
            setTimeout(() => this.applyProvinceFilter(), 0);
          });

          this.button.setAttribute('type', 'button');
          this.button.addEventListener('click', this.onCalculate.bind(this));
        }

        /* Merchant-configured list of selectable states (theme setting
           "shipping_calc_states"). Non-listed options stay visible but greyed
           out - deliberately indicative of states the business plans to serve
           - and the first allowed one is preselected. Countries whose
           provinces don't match the list at all (e.g. non-AU) are left
           untouched. */
        applyProvinceFilter() {
          if (!this.province) return;
          const allowed = (this.dataset.allowedProvinces || '')
            .split(',')
            .map((name) => name.trim().toLowerCase())
            .filter(Boolean);
          if (!allowed.length) return;

          const options = Array.from(this.province.options);
          const matches = options.filter((option) => allowed.includes(option.value.trim().toLowerCase()));
          if (!matches.length) {
            options.forEach((option) => (option.disabled = false));
            return;
          }

          options.forEach((option) => {
            option.disabled = !allowed.includes(option.value.trim().toLowerCase());
          });
          if (this.province.selectedOptions[0] && this.province.selectedOptions[0].disabled) {
            this.province.value = matches[0].value;
          }
        }

        async onCalculate() {
          if (this.message) this.message.hidden = true;

          let cartData;
          try {
            const cartResponse = await fetch('/cart.js');
            cartData = await cartResponse.json();
            if (cartData.item_count === 0) {
              this.showText(this.dataset.emptyCart, true);
              return;
            }
          } catch (error) {
            console.error('Error fetching cart data:', error);
            return;
          }

          /* Sample + other goods spans two delivery profiles; the legacy
             estimator endpoints merge that into one wrong "Shipping" line
             with the rate gating skipped. Re-checked here on every click
             against the live cart, so a line change (no server re-render)
             still lands on the right branch. The message template only
             renders while the shipping_calc_mixed_cart_guard setting is on,
             so its absence turns this check off. */
          if (this.mixedMessage && this.hasMixedSampleCart(cartData)) {
            this.showMixedCartMessage();
            return;
          }

          if (this.country.value === '---') {
            this.showText(this.dataset.countryPrompt, true);
            return;
          }

          this.showText(this.dataset.loading, false);
          this.button.disabled = true;
          this.button.classList.add('loading');

          const address = {
            zip: this.zip ? this.zip.value : '',
            country: this.country.value,
            province: this.province ? this.province.value : '',
          };

          try {
            const response = await fetchShippingRates(address, this.dataset.genericError);

            if (response.shipping_rates.length === 0) {
              this.results.innerHTML = `<p class="shipping-calculator__error">${this.dataset.noRates}</p>`;
              if (this.count) this.count.innerText = '0';
              if (this.message) this.message.hidden = true;
              this.trackNoRates(address, cartData);
              return;
            }

            this.results.innerHTML = '';
            response.shipping_rates.forEach((rate) => {
              const line = document.createElement('p');
              line.innerText = `${rate.name}: ${this.dataset.currencySymbol || ''}${rate.price}`;
              this.results.appendChild(line);
            });
            if (this.count) this.count.innerText = `${response.shipping_rates.length}`;
            if (this.message) this.message.hidden = false;
          } catch (error) {
            this.showText(error.message, true);
          } finally {
            this.button.disabled = false;
            this.button.classList.remove('loading');
          }
        }

        /* Mirrors the snippet's Liquid detection: _sample_product_handle is
           the theme convention, the title check the store's rates-gate rule */
        hasMixedSampleCart(cartData) {
          let hasSample = false;
          let hasOther = false;
          (cartData.items || []).forEach((item) => {
            const isSample =
              (item.properties && item.properties._sample_product_handle) ||
              (item.product_title || '').toLowerCase().includes('sample');
            if (isSample) {
              hasSample = true;
            } else {
              hasOther = true;
            }
          });
          return hasSample && hasOther;
        }

        /* Button stays enabled; on a mixed cart the click shows the
           explanatory message (rich text from the template, links intact)
           in the results area in place of rates. */
        showMixedCartMessage() {
          if (this.count) this.count.innerText = '0';
          if (this.message) this.message.hidden = true;
          if (this.results) this.results.innerHTML = this.mixedMessage.innerHTML;
        }

        /* Debug beacon: Shopify returning an empty rates array (the recurring
           stale-cart-token issue) is otherwise invisible in analytics. The
           cart token (key stripped) lets occurrences be correlated to a
           specific cart. */
        trackNoRates(address, cartData) {
          if (!window.themeAnalytics) return;
          const cookie = document.cookie.match(/(?:^|; )cart=([^;]+)/);
          const token = cookie ? decodeURIComponent(cookie[1]).split('?')[0] : '';
          window.themeAnalytics.trackEvent('shipping_rates_empty', {
            destination: [address.country, address.province, address.zip].filter(Boolean).join(' / '),
            cart_token: token,
            item_count: cartData ? cartData.item_count : null,
            cart_total: cartData ? cartData.total_price : null,
            cart_weight: cartData ? cartData.total_weight : null,
            logged_in: this.dataset.loggedIn === 'true',
            calculator_context: this.dataset.context || '',
          });
        }

        showText(text, isError) {
          if (!this.results) return;
          this.results.innerHTML = `<p${isError ? ' class="shipping-calculator__error"' : ''}></p>`;
          this.results.firstChild.innerText = text || '';
        }
      }
    );
  }
})();
